import { hasErrors } from "@/lib/functions/hasErrors";
import { getOrderToken } from '@/lib/functions/getOrderToken';
import { logMessage } from '@/lib/functions/logMessage';
import { publishCartRefresh } from '@/lib/broadcast/cartSync';
import { OrderState as OrderStateType } from '@/lib/types/orderState';
import { OrderActions } from '@/lib/context/orderActions';

interface RemoveFromCartResponse {
  success: boolean;
  message: string;
}

/**
 * Removes a product from the cart.
 *
 * @param {number} itemId - The ID of the item to remove.
 * @param {OrderStateType} orderState - The current state of the order.
 * @param {React.Dispatch<OrderActions>} orderDispatch - The dispatch function for order actions.
 * @returns {Promise<RemoveFromCartResponse>} - The response indicating the success or failure of the operation.
 */
export async function RemoveItemFromCart(
  itemId: number,
  orderState: OrderStateType,
  orderDispatch: React.Dispatch<OrderActions>
): Promise<RemoveFromCartResponse> {

  // Fetch the Cart Token
  const orderTokenAPIResponse = await getOrderToken(orderState, orderDispatch);
  const orderToken = orderTokenAPIResponse?.token;

  // Stop if there is no orderToken and we can't get a new one
  if (!orderToken) {
    return { success: false, message: 'Failed to remove from cart: No cart token available' };
  }

  try {
    // Call the API to delete the item
    const response = await fetch(`${process.env.NEXT_PUBLIC_SITE_URL}/api/orders/${orderToken}/items/${itemId}/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/ld+json',
      },
    });

    if (!response.ok) {
      // Try to parse error message from body (if any)
      let errorMessage = 'API returned error status';
      try {
        const errorBody = await response.json();
        if (hasErrors(errorBody)) {
          errorMessage = JSON.stringify(errorBody);
        }
      } catch {
        // no body to parse — skip
      }
      logMessage('Error in DELETE /api/orders: ' + errorMessage, 'error');
      return { success: false, message: 'Failed to remove from cart: API returned an error' };
    }

    publishCartRefresh(orderToken);

    // Return success
    return { success: true, message: 'Item removed from the cart successfully' };

  } catch (error: unknown) {
    if (error instanceof Error) {
      logMessage('Exception in DELETE /api/orders/{tokenValue}/items: ' + error.message, 'error');
    } else {
      logMessage('Unknown error in DELETE /api/orders/{tokenValue}/items', 'error');
    }
    return { success: false, message: 'Failed to remove from cart: Internal server error' };
  }
}
